import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./addCityHead.css";
import {
    editLocalityHead,
    getFranchiseManagerDetail,
    getCityHeadLocalityList,
    getCityHeadCityList,
    getCityHeadProjectList,
} from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import SearchableMultiSelect from "../../Global Functions/SearchableMultiSelect";

const EditLocalityHead = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { franchise_id, city_head_id, locality_head_id } = useParams();

    const {
        franchiseManagerDetail,
        detailLoading,
        cityList: rawCityList,
        localityList: rawLocalityList,
        projectList: rawProjectList,
        localityHeadEditing = false,
    } = useSelector((state) => state.franchiseManager || {});

    const cities = Array.isArray(rawCityList) ? rawCityList : [];
    const localities = Array.isArray(rawLocalityList) ? rawLocalityList : [];
    const projects = Array.isArray(rawProjectList) ? rawProjectList : [];

    const [errors, setErrors] = useState({});
    const [hydrated, setHydrated] = useState(false);
    const [localityHeadData, setLocalityHeadData] = useState({
        name: "",
        email: "",
        country_code: "+91",
        contact_number: "",
        city: [],
        locality: [],
        project: [],
    });

    useEffect(() => {
        if (locality_head_id) {
            dispatch(getFranchiseManagerDetail(locality_head_id));
        }
    }, [locality_head_id, dispatch]);

    useEffect(() => {
        const parentCityHeadId = franchiseManagerDetail?.city_head?._id || franchiseManagerDetail?.city_head || city_head_id;
        if (parentCityHeadId) {
            dispatch(getCityHeadCityList({ city_head_id: parentCityHeadId }, false));
            dispatch(getCityHeadLocalityList({ city_head_id: parentCityHeadId }, false));
            dispatch(getCityHeadProjectList({ city_head_id: parentCityHeadId }, false));
        }
    }, [franchiseManagerDetail, city_head_id, dispatch]);

    useEffect(() => {
        if (franchiseManagerDetail && franchiseManagerDetail._id === locality_head_id && !hydrated) {
            setLocalityHeadData({
                name: franchiseManagerDetail.name || "",
                email: franchiseManagerDetail.email || "",
                country_code: franchiseManagerDetail.country_code || "+91",
                city: (franchiseManagerDetail.city || []).map((c) => c._id || c),
                contact_number: franchiseManagerDetail.contact_number || "",
                locality: (franchiseManagerDetail.locality || []).map((l) => l._id || l),
                project: (franchiseManagerDetail.project || []).map((p) => p._id || p),
            });
            setHydrated(true);
        }
    }, [franchiseManagerDetail, locality_head_id, hydrated]);

    const cityOptions = useMemo(
        () => cities.map((c) => ({ value: c._id, label: c.city_name })),
        [cities]
    );

    const localityOptions = useMemo(() => {
        return localities
            .filter((loc) => localityHeadData.city.length === 0 || localityHeadData.city.includes(loc.city))
            .map((loc) => ({
                value: loc._id,
                label: loc.name,
                hint: loc.available === false ? "Taken" : undefined,
                disabled: loc.available === false && !franchiseManagerDetail?.locality?.some((l) => (l._id || l) === loc._id),
            }));
    }, [localities, localityHeadData.city, franchiseManagerDetail]);

    const projectOptions = useMemo(() => {
        return projects
            .filter((p) => {
                const projLocalityIds = (p.localities || []).map((l) => l._id || l);
                return projLocalityIds.some((lid) => localityHeadData.locality.includes(lid));
            })
            .map((p) => ({
                value: p._id,
                label: p.project_name,
                group: p.city?.city_name,
                hint: p.available === false ? "Taken" : undefined,
                disabled: p.available === false && !franchiseManagerDetail?.project?.some((pr) => (pr._id || pr) === p._id),
            }));
    }, [projects, localityHeadData.locality, franchiseManagerDetail]);

    const clearError = (field) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setLocalityHeadData((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    const handleCityChange = (values) => {
        setLocalityHeadData((prev) => ({
            ...prev,
            city: values,
            locality: prev.locality.filter((locId) => {
                const loc = localities.find((l) => l._id === locId);
                return loc && (values.length === 0 || values.includes(loc.city));
            }),
        }));
        clearError("city");
    };

    const handleLocalityChange = (values) => {
        setLocalityHeadData((prev) => {
            const autoSelected = projects
                .filter((p) => {
                    if (p.available === false) return false;
                    const projLocalityIds = (p.localities || []).map((l) => l._id || l);
                    return projLocalityIds.some((lid) => values.includes(lid));
                })
                .map((p) => p._id);

            const kept = prev.project.filter((projId) => {
                const proj = projects.find((p) => p._id === projId);
                if (!proj) return false;
                const projLocalityIds = (proj.localities || []).map((l) => l._id || l);
                return projLocalityIds.some((lid) => values.includes(lid));
            });

            return {
                ...prev,
                locality: values,
                project: values.length === 0 ? [] : Array.from(new Set([...kept, ...autoSelected])),
            };
        });
        clearError("locality");
    };

    const handleProjectChange = (values) => {
        setLocalityHeadData((prev) => ({ ...prev, project: values }));
        clearError("project");
    };

    const validate = () => {
        const newErrors = {};
        if (!localityHeadData.name.trim()) newErrors.name = "Name is required";
        if (!localityHeadData.email.trim()) newErrors.email = "Email is required";
        if (!localityHeadData.contact_number.trim()) newErrors.contact_number = "Contact number is required";
        if (!localityHeadData.locality.length) newErrors.locality = "At least one locality is required";
        if (!localityHeadData.project.length) newErrors.project = "At least one project is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const listPath = `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads`;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        try {
            const payload = {
                id: locality_head_id,
                name: localityHeadData.name,
                email: localityHeadData.email,
                country_code: localityHeadData.country_code,
                contact_number: localityHeadData.contact_number,
                city: localityHeadData.city,
                locality: localityHeadData.locality,
                project: localityHeadData.project,
            };

            const response = await dispatch(editLocalityHead(payload));

            if (response?.statusCode === 200) {
                toast.success("Locality Head updated successfully!");
                navigate(listPath);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || error?.message || "Failed to update locality head";

            if (errorMessage.toLowerCase().includes("duplicate") || errorMessage.toLowerCase().includes("already")) {
                setErrors((prev) => ({ ...prev, email: "Email already exists" }));
                return toast.error("Email already exists");
            }

            toast.error(errorMessage);
        }
    };

    return (
        <MainLayout>
            <div className="ach-wrapper">
                <div className="ach-header">
                    <div className="ach-header-left">
                        <div className="ach-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="ach-title">Edit Locality Head</h1>
                            <p className="ach-subtitle">Update details for this locality head</p>
                        </div>
                    </div>

                    <Link to={listPath} className="ach-view-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 21h18" />
                            <path d="M5 21V7l7-4 7 4v14" />
                        </svg>
                        Back to City Head
                    </Link>
                </div>

                <form className="ach-form" onSubmit={handleSubmit}>
                    <div className="ach-section">
                        <div className="ach-section-header">
                            <span className="ach-section-badge">01</span>
                            <h2 className="ach-section-title">Locality Head Details</h2>
                        </div>

                        <div className="ach-grid ach-grid-2">
                            <div className="ach-field">
                                <label className="ach-label">
                                    Name <span className="ach-req">*</span>
                                </label>
                                <input
                                    className={`ach-input ${errors.name ? "ach-input--error" : ""}`}
                                    type="text"
                                    name="name"
                                    value={localityHeadData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Priya Verma"
                                    disabled={localityHeadEditing || detailLoading}
                                />
                                {errors.name && <span className="ach-error-msg">{errors.name}</span>}
                            </div>

                            <div className="ach-field">
                                <label className="ach-label">
                                    Email <span className="ach-req">*</span>
                                </label>
                                <input
                                    className={`ach-input ${errors.email ? "ach-input--error" : ""}`}
                                    type="email"
                                    name="email"
                                    value={localityHeadData.email}
                                    onChange={handleChange}
                                    placeholder="e.g. priya@example.com"
                                    disabled={localityHeadEditing || detailLoading}
                                />
                                {errors.email && <span className="ach-error-msg">{errors.email}</span>}
                            </div>

                            <div className="ach-field">
                                <label className="ach-label">
                                    Country Code <span className="ach-req">*</span>
                                </label>
                                <select
                                    className="ach-select"
                                    name="country_code"
                                    value={localityHeadData.country_code}
                                    onChange={handleChange}
                                    disabled={localityHeadEditing || detailLoading}
                                >
                                    <option value="+91">India (+91)</option>
                                    <option value="+1">USA (+1)</option>
                                    <option value="+971">UAE (+971)</option>
                                </select>
                            </div>

                            <div className="ach-field">
                                <label className="ach-label">
                                    Contact Number <span className="ach-req">*</span>
                                </label>
                                <input
                                    className={`ach-input ${errors.contact_number ? "ach-input--error" : ""}`}
                                    type="text"
                                    name="contact_number"
                                    value={localityHeadData.contact_number}
                                    onChange={handleChange}
                                    placeholder="e.g. 9876543211"
                                    disabled={localityHeadEditing || detailLoading}
                                />
                                {errors.contact_number && <span className="ach-error-msg">{errors.contact_number}</span>}
                            </div>

                            <SearchableMultiSelect
                                label="City"
                                placeholder="Search cities..."
                                disabled={localityHeadEditing || detailLoading || cityOptions.length === 0}
                                options={cityOptions}
                                selected={localityHeadData.city}
                                onChange={handleCityChange}
                                error={errors.city}
                                emptyMessage="No cities assigned to this city head"
                            />

                            <div className="ach-field-with-action">
                                <button
                                    type="button"
                                    className="ach-select-all-btn"
                                    onClick={() => {
                                        const availableValues = localityOptions.filter((l) => !l.disabled).map((l) => l.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => localityHeadData.locality.includes(v));
                                        handleLocalityChange(allSelected ? [] : availableValues);
                                    }}
                                    disabled={localityHeadEditing || detailLoading || localityOptions.filter((l) => !l.disabled).length === 0}
                                >
                                    {(() => {
                                        const availableValues = localityOptions.filter((l) => !l.disabled).map((l) => l.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => localityHeadData.locality.includes(v));
                                        return allSelected ? "Unselect All" : "Select All";
                                    })()}
                                </button>

                                <SearchableMultiSelect
                                    label="Locality"
                                    required
                                    placeholder="Search localities..."
                                    disabled={localityHeadEditing || detailLoading || localityOptions.length === 0}
                                    options={localityOptions}
                                    selected={localityHeadData.locality}
                                    onChange={handleLocalityChange}
                                    error={errors.locality}
                                    emptyMessage="No localities assigned to this city head"
                                />
                            </div>

                            <div className="ach-field-with-action">
                                <button
                                    type="button"
                                    className="ach-select-all-btn"
                                    onClick={() => {
                                        const availableValues = projectOptions.filter((p) => !p.disabled).map((p) => p.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => localityHeadData.project.includes(v));
                                        handleProjectChange(allSelected ? [] : availableValues);
                                    }}
                                    disabled={
                                        localityHeadData.locality.length === 0 ||
                                        localityHeadEditing ||
                                        detailLoading ||
                                        projectOptions.filter((p) => !p.disabled).length === 0
                                    }
                                >
                                    {(() => {
                                        const availableValues = projectOptions.filter((p) => !p.disabled).map((p) => p.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => localityHeadData.project.includes(v));
                                        return allSelected ? "Unselect All" : "Select All";
                                    })()}
                                </button>

                                <SearchableMultiSelect
                                    label="Project"
                                    required
                                    placeholder={localityHeadData.locality.length === 0 ? "Select locality first" : "Search projects..."}
                                    disabled={localityHeadData.locality.length === 0 || localityHeadEditing || detailLoading}
                                    options={projectOptions}
                                    selected={localityHeadData.project}
                                    onChange={handleProjectChange}
                                    error={errors.project}
                                    emptyMessage="No projects for the selected locality"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ach-footer">
                        <button type="submit" className="ach-btn ach-btn--submit" disabled={localityHeadEditing || detailLoading}>
                            {localityHeadEditing ? "Saving..." : "Save Changes"}
                        </button>

                        <Link to={listPath} className="ach-btn ach-btn--cancel">
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default EditLocalityHead;